import React, { useState } from 'react';
import { Shield, CheckCircle, FileText, ExternalLink, AlertCircle, BookOpen } from 'lucide-react';
import api from '../api/client';

export default function PolicyAcknowledgementModal({ pendingAcks, onDone }) {
  // Group by policy so each unique policy appears once
  const policies = [];
  const seen = new Set();
  for (const ack of pendingAcks) {
    const pol = ack.policy || { id: ack.policy_id, name: ack.policy_name, type: ack.policy_type };
    if (!seen.has(pol.id)) { seen.add(pol.id); policies.push(pol); }
  }

  const [checked, setChecked] = useState({});
  const [iframeError, setIframeError] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const allChecked = policies.length > 0 && policies.every(p => checked[p.id]);

  // Pass JWT as query param — iframes don't send Authorization headers
  const token = localStorage.getItem('token');
  const viewUrl = (pol) => `/api/policies/view/${pol.id}?token=${token}#toolbar=0&navpanes=0`;

  const handleAcknowledge = async () => {
    setSubmitting(true); setError('');
    try {
      await api.post('/policies/acknowledge-all');
      onDone();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save acknowledgement. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(6,11,24,0.97)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px',
    }}>
      <div style={{
        background: 'linear-gradient(135deg,#0d1528,#0a1020)',
        border: '1px solid rgba(59,130,246,0.3)',
        borderRadius: 20,
        width: '100%', maxWidth: 720,
        maxHeight: '92vh',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 32px 100px rgba(0,0,0,0.8)',
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg,#1d4ed8,#4f46e5)',
          padding: '20px 28px',
          flexShrink: 0,
        }}>
          <div className="flex items-center gap-3">
            <div style={{background:'rgba(255,255,255,0.15)',borderRadius:10,padding:8}}>
              <Shield size={20} color="#fff"/>
            </div>
            <div>
              <div style={{color:'#fff',fontWeight:700,fontSize:16}}>IT Policy Acknowledgement Required</div>
              <div style={{color:'rgba(255,255,255,0.7)',fontSize:12,marginTop:2}}>
                Please review and acknowledge the following IT policies before continuing
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{overflowY:'auto',flex:1,padding:'24px 28px'}} className="space-y-6">
          <div style={{color:'#94a3b8',fontSize:13,lineHeight:1.6}}>
            As part of CL Synergy Group's IT governance, you are required to review and acknowledge the following policy documents.
            Please read each document carefully and confirm you have understood its contents.
          </div>

          {policies.map((pol) => (
            <div key={pol.id} style={{
              background:'rgba(255,255,255,0.03)',
              border:`1px solid ${checked[pol.id] ? 'rgba(16,185,129,0.4)' : 'rgba(255,255,255,0.08)'}`,
              borderRadius:14,
              overflow:'hidden',
              transition:'border-color 0.2s',
            }}>
              {/* Policy title bar */}
              <div className="flex items-center justify-between" style={{padding:'14px 18px',background:'rgba(255,255,255,0.02)'}}>
                <div className="flex items-center gap-2">
                  <FileText size={15} style={{color:'#818cf8'}}/>
                  <span style={{color:'#e2e8f0',fontWeight:600,fontSize:14}}>{pol.name}</span>
                </div>
                <a href={viewUrl(pol)} target="_blank" rel="noreferrer"
                  className="flex items-center gap-1.5 text-xs"
                  style={{color:'#60a5fa',textDecoration:'none'}}>
                  <ExternalLink size={11}/> Open in new tab
                </a>
              </div>

              {/* PDF Viewer */}
              <div style={{position:'relative',background:'#1a1a2e'}}>
                {!iframeError[pol.id] ? (
                  <iframe
                    src={viewUrl(pol)}
                    title={pol.name}
                    style={{width:'100%',height:420,border:'none',display:'block'}}
                    onError={() => setIframeError(prev => ({...prev, [pol.id]: true}))}
                    onLoad={e => {
                      try {
                        const doc = e.target.contentDocument || e.target.contentWindow?.document;
                        const text = doc?.body?.innerText || '';
                        if (text.includes('ENOENT') || text.includes('"error"')) {
                          setIframeError(prev => ({...prev, [pol.id]: true}));
                        }
                      } catch {}
                    }}
                  />
                ) : (
                  <div style={{padding:'32px 24px',textAlign:'center',background:'rgba(245,158,11,0.05)',borderTop:'1px solid rgba(245,158,11,0.1)'}}>
                    <AlertCircle size={28} style={{color:'#f59e0b',marginBottom:12}}/>
                    <div style={{color:'#e2e8f0',fontSize:14,fontWeight:600,marginBottom:6}}>Unable to display PDF inline</div>
                    <div style={{color:'#475569',fontSize:12,marginBottom:16}}>Please open the document in a new tab to read it before acknowledging.</div>
                    <a href={viewUrl(pol)} target="_blank" rel="noreferrer"
                      style={{display:'inline-flex',alignItems:'center',gap:6,background:'rgba(59,130,246,0.15)',color:'#60a5fa',padding:'8px 18px',borderRadius:8,fontSize:13,textDecoration:'none',border:'1px solid rgba(59,130,246,0.3)'}}>
                      <ExternalLink size={13}/> Open {pol.name} in New Tab
                    </a>
                  </div>
                )}
              </div>

              {/* Confirmation checkbox */}
              <div style={{padding:'14px 18px',borderTop:'1px solid rgba(255,255,255,0.06)'}}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <div style={{position:'relative',flexShrink:0,marginTop:2}}>
                    <input
                      type="checkbox"
                      checked={!!checked[pol.id]}
                      onChange={e => setChecked(prev => ({...prev, [pol.id]: e.target.checked}))}
                      style={{opacity:0,position:'absolute',inset:0,cursor:'pointer',zIndex:1,width:18,height:18}}
                    />
                    <div style={{
                      width:18, height:18, borderRadius:5,
                      background: checked[pol.id] ? '#10b981' : 'rgba(255,255,255,0.08)',
                      border: `2px solid ${checked[pol.id] ? '#10b981' : 'rgba(255,255,255,0.2)'}`,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      transition:'all 0.15s',
                    }}>
                      {checked[pol.id] && <CheckCircle size={11} color="#fff"/>}
                    </div>
                  </div>
                  <span style={{color: checked[pol.id] ? '#34d399' : '#94a3b8', fontSize:13, lineHeight:1.5}}>
                    I have read and understood the <strong style={{color: checked[pol.id] ? '#34d399' : '#e2e8f0'}}>{pol.name}</strong> and agree to comply with its requirements.
                  </span>
                </label>
              </div>
            </div>
          ))}

          {/* Post-acknowledge reference note */}
          <div style={{
            background:'rgba(99,102,241,0.06)',
            border:'1px solid rgba(99,102,241,0.2)',
            borderRadius:12,
            padding:'14px 18px',
            display:'flex',
            alignItems:'flex-start',
            gap:12,
          }}>
            <BookOpen size={16} style={{color:'#818cf8',flexShrink:0,marginTop:1}}/>
            <div>
              <div style={{color:'#a5b4fc',fontSize:13,fontWeight:600,marginBottom:3}}>Refer to IT Policies anytime</div>
              <div style={{color:'#64748b',fontSize:12,lineHeight:1.6}}>
                After acknowledging, you can refer to these policy documents at any time via the <strong style={{color:'#818cf8'}}>IT Policies</strong> section in your user portal (<strong style={{color:'#818cf8'}}>My Portal → My Policies</strong>). Your acknowledgement record will also be shown there.
              </div>
            </div>
          </div>

          {error && (
            <div style={{background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.25)',borderRadius:10,padding:'10px 14px',color:'#f87171',fontSize:13}}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding:'16px 28px',
          borderTop:'1px solid rgba(255,255,255,0.08)',
          background:'rgba(0,0,0,0.2)',
          flexShrink:0,
        }}>
          <div className="flex items-center justify-between gap-4">
            <div style={{color:'#475569',fontSize:12}}>
              {policies.filter(p => checked[p.id]).length} of {policies.length} policies confirmed
            </div>
            <button
              onClick={handleAcknowledge}
              disabled={!allChecked || submitting}
              style={{
                background: allChecked ? 'linear-gradient(135deg,#10b981,#059669)' : 'rgba(255,255,255,0.05)',
                color: allChecked ? '#fff' : '#334155',
                border: 'none', borderRadius:10,
                padding:'11px 28px',
                fontWeight:700, fontSize:14,
                cursor: allChecked ? 'pointer' : 'not-allowed',
                display:'flex', alignItems:'center', gap:8,
                transition:'all 0.2s',
                opacity: submitting ? 0.7 : 1,
              }}>
              <CheckCircle size={16}/>
              {submitting ? 'Saving...' : 'I Acknowledge All Policies'}
            </button>
          </div>
          {!allChecked && (
            <div style={{color:'#475569',fontSize:11,marginTop:6,textAlign:'right'}}>
              Please read and confirm all policies above to proceed
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
